import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import {
  clearClock,
  hydrateActive,
  reset,
  setActiveLabel,
  setActiveReadingId,
  setisRunning,
  setSessions,
  setTime,
} from "@features/annogram/navbar/state/stopwatchSlice";
import { setUserData } from "@store/slices/workspaceSlice";
import { selectCurrentReading } from "@store/selectors";
import { selectIsDemoMode } from "@store/slices/viewSlice";
import Dialog from "@mui/material/Dialog";
import {
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  Stack,
  TextField,
  Tooltip,
  useTheme,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import {
  AccessAlarm,
  Add,
  Close,
  EditOutlined,
  Pause,
  PlayArrow,
  Replay,
} from "@mui/icons-material";
import DialogContent from "@mui/material/DialogContent";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import {
  clearActiveTimeSession,
  getReadingTimeSessions,
  logReadingTimeSession,
  renameTimeSession,
  saveActiveTimeSession,
  setTimeLog,
} from "@services/api";
import { formatHMS } from "../utils/formatHMS";
import { useNotification } from "@shared/contexts/NotificationContext";

interface Props {
  modalVisible: string;
  setModalVisible: Dispatch<SetStateAction<string>>;
}

// How often a running session flushes to the server. A closed tab
// loses at most this much running time.
const AUTOSAVE_MS = 30_000;

// A session name that flips into a small inline editor on the pencil
// click. Commit on Enter/blur; Escape cancels.
function EditableName({
  value,
  onCommit,
  ariaLabel,
}: {
  value: string;
  onCommit: (next: string) => void;
  ariaLabel: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  if (!editing) {
    return (
      <Stack direction="row" alignItems="center" spacing={0.25}>
        <Typography variant="body2" noWrap>
          {value}
        </Typography>
        <IconButton
          size="small"
          aria-label={ariaLabel}
          onClick={() => {
            setDraft(value);
            setEditing(true);
          }}
          sx={{ padding: 0.25 }}
        >
          <EditOutlined sx={{ fontSize: 14 }} />
        </IconButton>
      </Stack>
    );
  }

  const commit = () => {
    setEditing(false);
    const next = draft.trim();
    if (next && next !== value) onCommit(next);
  };

  return (
    <TextField
      variant="standard"
      size="small"
      value={draft}
      autoFocus
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit();
        if (e.key === "Escape") setEditing(false);
      }}
      inputProps={{ maxLength: 100, "aria-label": ariaLabel }}
    />
  );
}

const StopWatchTest = ({ modalVisible, setModalVisible }: Props) => {
  const workspaceState = useAppSelector((state) => state.workspace);
  const stopwatchState = useAppSelector((state) => state.stopwatch);
  const currentReading = useAppSelector(selectCurrentReading);
  const isDemoMode = useAppSelector(selectIsDemoMode);
  const dispatch = useAppDispatch();

  const theme = useTheme();
  const { showError } = useNotification();
  const open = modalVisible === "watch";
  const handleClose = () => setModalVisible("");

  const readingId = currentReading?.reading_id ?? null;
  // Only render sessions that belong to the current reading — the cache
  // may still hold the previous reading's list right after a switch.
  const sessions =
    readingId && stopwatchState.sessionsReadingId === readingId
      ? stopwatchState.sessions
      : [];

  const { time, isRunning, activeLabel } = stopwatchState;
  const [confirmRestart, setConfirmRestart] = useState(false);

  // Latest values for effects that must not re-run on every tick
  // (auto-save interval, reading-switch flush).
  const latest = useRef({ time, activeLabel, workspaceId: "", isDemoMode });
  latest.current = {
    time,
    activeLabel,
    workspaceId: workspaceState.workspace_id,
    isDemoMode,
  };
  const activeReadingRef = useRef<string | null>(null);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined;
    if (isRunning) {
      interval = setInterval(() => {
        dispatch(setTime());
      }, 1000);
    } else if (!isRunning && interval) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [dispatch, isRunning]);

  // Reading switch: flush the in-progress session to the reading it
  // belongs to, zero the clock (a running timer keeps running into the
  // new reading), then load the new reading's sessions and resume its
  // saved in-progress session if one exists. Also runs on mount, which
  // is how a session survives a page reload.
  useEffect(() => {
    const prev = activeReadingRef.current;
    const snap = latest.current;
    if (prev && prev !== readingId && snap.time > 0 && !snap.isDemoMode) {
      saveActiveTimeSession(snap.workspaceId, prev, {
        label: snap.activeLabel.trim() || "Session",
        seconds: snap.time,
      });
    }
    if (prev && prev !== readingId) {
      dispatch(clearClock());
    }
    activeReadingRef.current = readingId;
    dispatch(setActiveReadingId(readingId));

    if (!readingId || snap.isDemoMode) return;
    let cancelled = false;
    (async () => {
      const result = await getReadingTimeSessions(snap.workspaceId, readingId);
      if (cancelled || !result.success) return;
      dispatch(
        setSessions({ readingId, sessions: result.data.time_sessions })
      );
      const active = result.data.active_session;
      if (active) {
        dispatch(
          hydrateActive({
            readingId,
            label: active.label,
            seconds: active.seconds,
          })
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [readingId, dispatch]);

  // Periodic auto-save while running.
  useEffect(() => {
    if (!isRunning) return;
    const id = setInterval(() => {
      const snap = latest.current;
      const rid = activeReadingRef.current;
      if (!rid || snap.isDemoMode || snap.time === 0) return;
      saveActiveTimeSession(snap.workspaceId, rid, {
        label: snap.activeLabel.trim() || "Session",
        seconds: snap.time,
      });
    }, AUTOSAVE_MS);
    return () => clearInterval(id);
  }, [isRunning]);

  // Suggest the next session name — only for a fresh clock, so a
  // custom or restored in-progress name is never clobbered.
  useEffect(() => {
    if (!open || time !== 0) return;
    dispatch(setActiveLabel(`Session ${sessions.length + 1}`));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, readingId, sessions.length, time === 0]);

  const readingTotal = useMemo(
    () => sessions.reduce((acc, s) => acc + s.seconds, 0),
    [sessions]
  );

  const canRoll = !!readingId && time > 0;

  const handleToggleRunning = () => {
    // Pausing flushes the session so far.
    if (isRunning && time > 0 && readingId && !isDemoMode) {
      saveActiveTimeSession(workspaceState.workspace_id, readingId, {
        label: activeLabel.trim() || "Session",
        seconds: time,
      }).then((result) => {
        if (!result.success) {
          showError(
            "Couldn't save the session's progress — the time is still on the clock, but won't survive a reload.",
            "Timer"
          );
        }
      });
    }
    dispatch(setisRunning());
  };

  // Finalize the current session and roll straight into the next one —
  // a running timer keeps running.
  const handleNewSession = async () => {
    if (!canRoll || !readingId) return;
    const seconds = time;
    const trimmedLabel = activeLabel.trim() || `Session ${sessions.length + 1}`;

    const result = await logReadingTimeSession(
      workspaceState.workspace_id,
      readingId,
      { label: trimmedLabel, seconds }
    );
    if (!result.success) {
      // Keep the clock so the session isn't lost on a failed save.
      showError(
        "Couldn't save the session — your time is still on the clock. Check your connection and try again.",
        "Timer"
      );
      return;
    }
    dispatch(setSessions({ readingId, sessions: result.data.time_sessions }));
    dispatch(clearClock());
    // Bump the workspace-level total too — the Dashboard's total-time
    // stat aggregates user_data.timeLog per workspace.
    const newTotal = (workspaceState.user_data?.timeLog ?? 0) + seconds;
    await setTimeLog(workspaceState.workspace_id, newTotal);
    dispatch(setUserData({ timeLog: newTotal }));
  };

  const handleRenameSession = async (index: number, label: string) => {
    if (!readingId) return;
    if (isDemoMode) {
      dispatch(
        setSessions({
          readingId,
          sessions: sessions.map((s, i) =>
            i === index ? { ...s, label } : s
          ),
        })
      );
      return;
    }
    const result = await renameTimeSession(
      workspaceState.workspace_id,
      readingId,
      index,
      label
    );
    if (!result.success) {
      showError("Couldn't rename the session.", "Timer");
      return;
    }
    dispatch(setSessions({ readingId, sessions: result.data.time_sessions }));
  };

  const handleDiscard = () => {
    dispatch(reset());
    setConfirmRestart(false);
    if (!isDemoMode && readingId) {
      clearActiveTimeSession(workspaceState.workspace_id, readingId);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth={true}
      sx={{
        "& .MuiDialog-container": {
          alignItems: "flex-start",
          marginLeft: "20px",
          marginTop: "40px",
        },
        "& .MuiDialog-paper": {
          background: theme.palette.background.paper,
          borderRadius: 2,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          paddingX: 2.5,
          paddingY: 1.5,
        }}
      >
        <AccessAlarm
          fontSize="small"
          color={isRunning ? "success" : "action"}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ lineHeight: 1.2 }}>
            Timer
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {currentReading
              ? currentReading.reading_name
              : "Select a reading to log time"}
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ marginLeft: "auto" }}
          aria-label="Close timer"
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>
      <Divider />

      <DialogContent sx={{ paddingTop: 2, paddingBottom: 2 }}>
        {/* Clock */}
        <Typography
          sx={{
            textAlign: "center",
            fontSize: 56,
            fontWeight: 300,
            fontVariantNumeric: "tabular-nums",
            letterSpacing: 2,
            lineHeight: 1.1,
            color: isRunning
              ? theme.palette.success.main
              : theme.palette.text.primary,
            transition: "color 200ms",
          }}
        >
          {formatHMS(time)}
        </Typography>

        {/* Controls */}
        <Stack spacing={1.5} sx={{ marginTop: 2 }}>
          <Stack direction="row" spacing={1} justifyContent="center">
            <Button
              variant="contained"
              color={isRunning ? "inherit" : "primary"}
              startIcon={isRunning ? <Pause /> : <PlayArrow />}
              onClick={handleToggleRunning}
              disabled={!readingId}
              sx={{ minWidth: "7.5em" }}
            >
              {isRunning ? "Pause" : time > 0 ? "Resume" : "Start"}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={handleNewSession}
              disabled={!canRoll}
              sx={{ minWidth: "8.5em" }}
            >
              New session
            </Button>
            <Tooltip title="Restart session">
              <span>
                <IconButton
                  onClick={() => setConfirmRestart(true)}
                  disabled={time === 0}
                  aria-label="Restart session"
                >
                  <Replay />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textAlign: "center" }}
          >
            Progress saves automatically — "New session" files the active
            one and keeps timing.
          </Typography>
        </Stack>

        {/* Logged sessions */}
        {readingId && (
          <Box sx={{ marginTop: 2 }}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ letterSpacing: 1 }}
            >
              Sessions
            </Typography>
            <List dense disablePadding>
              {/* The active session is a permanent first row — it
                  exists from 00:00:00 so the current state is always
                  visible, and its name edits in place. */}
              <ListItem disableGutters divider sx={{ paddingY: 0.5 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <EditableName
                      value={activeLabel.trim() || "Session"}
                      ariaLabel="Rename active session"
                      onCommit={(next) => dispatch(setActiveLabel(next))}
                    />
                    <Chip
                      label="Active"
                      size="small"
                      variant="outlined"
                      color="success"
                      sx={{ height: 18, fontSize: 11 }}
                    />
                  </Stack>
                  <Typography
                    variant="caption"
                    sx={{
                      color: isRunning
                        ? theme.palette.success.main
                        : theme.palette.text.secondary,
                    }}
                  >
                    {isRunning ? "Running" : time > 0 ? "Paused" : "Not started"}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontVariantNumeric: "tabular-nums",
                    color: isRunning
                      ? theme.palette.success.main
                      : undefined,
                  }}
                >
                  {formatHMS(time)}
                </Typography>
              </ListItem>
              {sessions.map((session, idx) => (
                <ListItem
                  key={`${session.logged_at}-${idx}`}
                  disableGutters
                  divider
                  sx={{ paddingY: 0.5 }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <EditableName
                      value={session.label}
                      ariaLabel={`Rename session ${session.label}`}
                      onCommit={(next) => handleRenameSession(idx, next)}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {new Date(session.logged_at).toLocaleDateString()}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {formatHMS(session.seconds)}
                  </Typography>
                </ListItem>
              ))}
            </List>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                paddingTop: 1,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Total
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {formatHMS(readingTotal + time)}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      {/* Restart guard — a session is easy to wipe by accident, so
          discarding is an explicit, cancel-colored act. */}
      <Dialog
        open={confirmRestart}
        onClose={() => setConfirmRestart(false)}
        maxWidth="xs"
        aria-labelledby="restart-session-title"
      >
        <Box sx={{ padding: 2.5 }}>
          <Typography id="restart-session-title" variant="subtitle1">
            Discard current session?
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ marginTop: 0.5 }}
          >
            The current session ({formatHMS(time)}) will be discarded and
            its time lost.
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            sx={{ marginTop: 2 }}
          >
            <Button
              variant="outlined"
              onClick={() => setConfirmRestart(false)}
            >
              Keep timing
            </Button>
            <Button variant="contained" color="cancel" onClick={handleDiscard}>
              Discard
            </Button>
          </Stack>
        </Box>
      </Dialog>
    </Dialog>
  );
};

export default StopWatchTest;
